/*
 * Spy Radio · native React player
 *
 * Iter 31.281 (8 Jul 2026) — Two spec'd this after her CMS-injected
 * player couldn't run: `<script>` tags in `body_md` are stripped by
 * rehype-raw's security sanitiser (correctly — the exact defence-in-
 * depth pattern iter 31.278 was supposed to sit on top of). So the
 * radio surface is now a first-class React route rendered natively at
 * `/spyradio/player` (preview) / `spyradio.uk/player` (production).
 *
 * Design brief — Two's aesthetic, faithfully:
 *   Palette:   near-black #0D1117 base · cold steel-blue #4A9EBF accent
 *   Type:      Space Grotesk (display) · JetBrains Mono (metadata)
 *   Signature: live audio spectrum visualiser pulsing with the mix
 *   Chrome:    minimal — three buttons, one now-playing card, cover art
 *
 * Fonts are loaded via `<link>` injected on mount so the rest of the
 * app's paper-and-ink stack isn't polluted with Space Grotesk / Mono
 * fallbacks that don't belong.
 *
 * Contract locked from Ern's brief (iter 31.280 handoff):
 *   Catalogue: GET /api/radio/station/tracks/public?station_key=SPY_RADIO_PUBLIC
 *   Stream:    GET /api/radio/station/stream/{id}?station_key=SPY_RADIO_PUBLIC
 *   Cover:     GET /api/radio/station/cover/{brand}.jpg (already prepend by API)
 *   Note:      NO url/file_url field on track objects — construct from id.
 *              CORS is `*` server-side; use crossOrigin=anonymous on <audio>
 *              so the AudioContext can tap the MediaElementSource for the
 *              spectrum without a CORS violation.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
// Iter 31.316 — station-driven. When a slug is present (via
// /player/<slug> or /player?station=<slug>) we hit the multi-station
// public catalogue; otherwise fall back to the legacy fixed
// SPY_RADIO_PUBLIC catalogue so `/player` on its own still works.
const STATION_KEY = "SPY_RADIO_PUBLIC";
const DEFAULT_BRAND = "spy_finance";
// Iter 31.316 — artwork fallback chain: track cover → station cover
// → default brand image. `resolveCover(track, station)` returns the
// first URL that isn't falsy. Absolute URLs pass through; API-relative
// paths are prefixed with the backend origin.
const DEFAULT_ARTWORK = `${API}/api/radio/station/cover/${DEFAULT_BRAND}.jpg`;

// Iter 31.317 — one reusable player engine, per-station identity.
// `_DEFAULT_COLOURS` is the base dark palette; every station identity
// can override any subset via `identity.palette`. Missing keys fall
// through to the defaults so callers only specify what changes.
const _DEFAULT_COLOURS = {
    bg: "#0D1117",
    accent: "#4A9EBF",
    accentDim: "#3A7A94",
    ink: "#E6EDF3",
    inkSoft: "#7D8590",
    line: "#21262D",
};

// Curated identity registry — one per known station slug. Extend by
// adding a row; falls back to `DEFAULT_IDENTITY` for anything else.
// `voice` is the short editorial line rendered under the title. Kept
// in-file rather than backend-served for now to avoid a round-trip
// on the player boot path; move to the /stations endpoint if the map
// grows past a handful of entries.
const DEFAULT_IDENTITY = {
    eyebrow: "Spy Radio · Live",
    name: "SPY / RADIO",
    voice: null,
    palette: _DEFAULT_COLOURS,
};

const STATION_IDENTITIES = {
    spy_finance: {
        eyebrow: "SpyFinance · On Air",
        name: "SPY FINANCE RADIO",
        voice: "Twenty-year horizon. Handler beat. Late-night dispatches.",
        palette: { bg: "#0D1117", accent: "#B22020", accentDim: "#7A1515", ink: "#E6EDF3", inkSoft: "#8A6666", line: "#3B1A1A" },
    },
    spy_health: {
        eyebrow: "SpyHealth · On Air",
        name: "SPY HEALTH RADIO",
        voice: "Six pillars, one signal. Calm operational feed.",
        palette: { bg: "#0A1710", accent: "#6BAF7A", accentDim: "#3E7749", ink: "#E6EDF3", inkSoft: "#7D9A85", line: "#1E3524" },
    },
    spy_sports: {
        eyebrow: "SpySports · On Air",
        name: "SPY SPORTS RADIO",
        voice: "Match nights, training decks, terrace history.",
        palette: { bg: "#0B141F", accent: "#E8A63A", accentDim: "#A87720", ink: "#E6EDF3", inkSoft: "#9AA7B5", line: "#2A3A4E" },
    },
    spy_career: {
        eyebrow: "SpyCareer · On Air",
        name: "SPY CAREER RADIO",
        voice: "Late-shift work rota for the eye that never sleeps.",
        palette: { bg: "#12100D", accent: "#B8860B", accentDim: "#8B5F08", ink: "#F5EFE3", inkSoft: "#9C8B6E", line: "#3A2E1E" },
    },
    spy_academy: {
        eyebrow: "SpyAcademy · On Air",
        name: "SPY ACADEMY RADIO",
        voice: "Curriculum tapes. Rule 15 canon. Recruit playback.",
        palette: { bg: "#0F0A1A", accent: "#8A6CD1", accentDim: "#5D4491", ink: "#E6EDF3", inkSoft: "#8D82A7", line: "#2B1F45" },
    },
};

function resolveIdentity(slug) {
    if (!slug) return DEFAULT_IDENTITY;
    const rec = STATION_IDENTITIES[slug];
    if (rec) return { ...DEFAULT_IDENTITY, ...rec, palette: { ..._DEFAULT_COLOURS, ...(rec.palette || {}) } };
    // Unknown slug — take the default palette but derive a display name
    // from the slug so the page doesn't look like it's mislabeled.
    const derived = (slug || "").replace(/[_-]+/g, " ").toUpperCase().trim();
    return {
        ...DEFAULT_IDENTITY,
        eyebrow: `${derived} · On Air`,
        name: derived || DEFAULT_IDENTITY.name,
    };
}

// Iter 31.318 — merge a Drive-served identity (from meta/station.json)
// over the resolved base identity. Palette is deep-merged so the JSON
// only needs to declare the keys it wants to override. Missing fields
// fall through to the base cleanly.
function mergeIdentity(base, override) {
    if (!override || typeof override !== "object") return base;
    const merged = { ...base };
    for (const key of ["eyebrow", "name", "voice"]) {
        if (typeof override[key] === "string" && override[key].trim()) {
            merged[key] = override[key];
        }
    }
    if (override.palette && typeof override.palette === "object") {
        merged.palette = { ...base.palette, ...override.palette };
    }
    return merged;
}

function resolveCover(track, station) {
    const raw = track?.cover_url || station?.cover_url;
    if (!raw) return DEFAULT_ARTWORK;
    if (raw.startsWith("http")) return raw;
    return `${API}${raw}`;
}

const COLOURS = _DEFAULT_COLOURS;  // iter 31.317 — kept as legacy alias so anything outside the component still resolves; new code uses `palette`.

function _shuffle(arr) {
    // Fisher-Yates so the ordering distribution is uniform. `sort()` with
    // a random comparator is not.
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}

function useDisplayFonts() {
    // Load Space Grotesk + JetBrains Mono once on mount, remove on
    // unmount so navigating away doesn't leave orphan <link> tags.
    useEffect(() => {
        const links = [
            "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&display=swap",
            "https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500&display=swap",
        ].map((href) => {
            const l = document.createElement("link");
            l.rel = "stylesheet";
            l.href = href;
            l.dataset.spyRadioFont = "1";
            document.head.appendChild(l);
            return l;
        });
        return () => links.forEach((l) => l.parentNode?.removeChild(l));
    }, []);
}

export default function RadioPlayer({ stationSlug: propSlug, identity: propIdentity } = {}) {
    useDisplayFonts();
    // Iter 31.317 — station-configured shared engine.
    //   • `propSlug` takes precedence (composed usage from a per-station page)
    //   • else the URL /player/:slug wins
    //   • else the ?station=<slug> query
    //   • else the legacy fixed SPY_RADIO_PUBLIC catalogue
    // Identity likewise: caller-supplied wins, else looked up in
    // STATION_IDENTITIES by slug, else the DEFAULT_IDENTITY.
    const { slug: slugParam } = useParams();
    const [searchParams] = useSearchParams();
    const stationSlug = propSlug || slugParam || searchParams.get("station") || null;
    // State (declared BEFORE the identity useMemo so `station` isn't in TDZ).
    const [station, setStation] = useState(null);   // { slug, name, cover_url, identity? } | null
    const [tracks, setTracks] = useState(null);
    const [queue, setQueue] = useState([]);       // shuffled remainder
    const [current, setCurrent] = useState(null); // track dict or null
    const [playing, setPlaying] = useState(false);
    const [muted, setMuted] = useState(false);
    const [error, setError] = useState("");
    const identity = useMemo(
        () => {
            if (propIdentity) return propIdentity;
            const base = resolveIdentity(stationSlug);
            // Iter 31.318 — if the multi-station endpoint returned a
            // Drive-served identity (via `meta/station.json`), layer it
            // over the static base so operator edits on Drive win.
            return mergeIdentity(base, station?.identity);
        },
        [propIdentity, stationSlug, station?.identity],
    );
    const palette = identity.palette;  // never null — resolveIdentity guarantees
    const audioRef = useRef(null);
    const canvasRef = useRef(null);
    const audioCtxRef = useRef(null);
    const analyserRef = useRef(null);
    const sourceRef = useRef(null);
    const rafRef = useRef(null);

    // 1. Fetch catalogue on mount / when station changes. Two paths:
    //    (a) station slug present → /api/spyradio/public/stations/<slug>/tracks
    //        which is Iter 31.288's multi-station Drive catalogue; each
    //        track object already carries `stream_url` so we don't need to
    //        rebuild the URL. Also gives us station metadata for the
    //        artwork fallback chain.
    //    (b) no slug → legacy fixed SPY_RADIO_PUBLIC brand catalogue.
    //    Filters drops from the visible playback pool (they still stream
    //    on the multi-station page; here the /player surface is
    //    catalogue-driven, so we keep the mix pure).
    useEffect(() => {
        let cancelled = false;
        setTracks(null);
        setCurrent(null);
        setQueue([]);
        setError("");
        (async () => {
            try {
                if (stationSlug) {
                    const { data } = await axios.get(
                        `${API}/api/spyradio/public/stations/${encodeURIComponent(stationSlug)}/tracks`,
                    );
                    if (cancelled) return;
                    setStation(data?.station || null);
                    const raw = data?.tracks || [];
                    const list = raw
                        .filter((t) => (t?.tag || "").toLowerCase() !== "drop")
                        .map((t) => ({ ...t, _source: "multi_station" }));
                    setTracks(list);
                    if (list.length) {
                        const [first, ...rest] = _shuffle(list);
                        setCurrent(first);
                        setQueue(rest);
                    }
                } else {
                    const { data } = await axios.get(
                        `${API}/api/radio/station/tracks/public`,
                        { params: { station_key: STATION_KEY, brand: DEFAULT_BRAND } },
                    );
                    if (cancelled) return;
                    setStation(null);
                    const list = (data.tracks || []).map((t) => ({ ...t, _source: "legacy" }));
                    setTracks(list);
                    if (list.length) {
                        const [first, ...rest] = _shuffle(list);
                        setCurrent(first);
                        setQueue(rest);
                    }
                }
            } catch (e) {
                if (!cancelled) setError(e.response?.data?.detail || e.message || "catalogue fetch failed");
            }
        })();
        return () => { cancelled = true; };
    }, [stationSlug]);

    // 2. Whenever `current` changes, point the <audio> at the new stream.
    //    crossOrigin MUST be set BEFORE src assignment so the eventual
    //    MediaElementSource tap doesn't trip a CORS violation.
    //
    //    Iter 31.286 (Two's spec, 09 Jul 2026) — race-free hand-off.
    //    The previous implementation called `.play()` synchronously
    //    after setting `.src`, which raced against the browser's
    //    async load: when a track ended and the queue advanced, the
    //    still-pending play() from the *previous* src would be aborted
    //    by the new load, surfacing as "AbortError: The play() request
    //    was interrupted by a new load request". Fix: wait for the
    //    `canplay` event before firing play(), and swallow the AbortError
    //    class of failures (they're expected mid-transition, not user
    //    errors). Effect cleanup removes the pending listener so a
    //    rapid re-advance doesn't stack them.
    useEffect(() => {
        if (!current || !audioRef.current) return undefined;
        const el = audioRef.current;
        // Iter 31.316 — multi-station tracks carry `stream_url`; legacy
        // catalogue rows don't, so we rebuild from id + station_key.
        const url = current.stream_url
            ? `${API}${current.stream_url}`
            : `${API}/api/radio/station/stream/${current.id}?station_key=${STATION_KEY}`;
        el.crossOrigin = "anonymous";
        el.src = url;
        el.load();

        if (!playing) return undefined;

        let cancelled = false;
        const attemptPlay = () => {
            if (cancelled) return;
            const p = el.play();
            if (p && typeof p.catch === "function") {
                p.catch((err) => {
                    // AbortError: interrupted by a new load — expected
                    // mid-transition, silent. NotAllowedError: autoplay
                    // blocked, leave paused. Anything else: surface.
                    if (err && (err.name === "AbortError"
                                || err.name === "NotAllowedError")) return;
                    setError(`playback blocked: ${err?.message || err}`);
                });
            }
        };
        // readyState >= 3 (HAVE_FUTURE_DATA) means playback can start.
        if (el.readyState >= 3) {
            attemptPlay();
            return () => { cancelled = true; };
        }
        const onCanPlay = () => {
            el.removeEventListener("canplay", onCanPlay);
            attemptPlay();
        };
        el.addEventListener("canplay", onCanPlay);
        return () => {
            cancelled = true;
            el.removeEventListener("canplay", onCanPlay);
        };
    }, [current, playing]);

    // 3. Wire the Web Audio graph on first play. Lazy so a passive load
    //    doesn't create an audio context until the operator asks for
    //    sound (Chrome autoplay policy).
    const _ensureAnalyser = useCallback(() => {
        if (audioCtxRef.current || !audioRef.current) return;
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return;
        const ctx = new AC();
        const source = ctx.createMediaElementSource(audioRef.current);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.82;
        source.connect(analyser);
        analyser.connect(ctx.destination);
        audioCtxRef.current = ctx;
        analyserRef.current = analyser;
        sourceRef.current = source;
    }, []);

    // 4. Spectrum draw loop. Idle when not playing so we don't burn CPU.
    useEffect(() => {
        const draw = () => {
            const canvas = canvasRef.current;
            const analyser = analyserRef.current;
            if (!canvas || !analyser) {
                rafRef.current = requestAnimationFrame(draw);
                return;
            }
            const w = canvas.width;
            const h = canvas.height;
            const ctx = canvas.getContext("2d");
            ctx.clearRect(0, 0, w, h);
            const bins = analyser.frequencyBinCount;
            const data = new Uint8Array(bins);
            analyser.getByteFrequencyData(data);
            // 64 bars, log-ish frequency mapping so highs get more real
            // estate than the FFT's linear allocation gives them.
            const BARS = 64;
            const barW = w / BARS;
            for (let i = 0; i < BARS; i++) {
                const t = i / BARS;
                const idx = Math.floor(Math.pow(t, 1.6) * bins);
                const v = data[idx] / 255;
                const barH = Math.max(2, v * h);
                const y = h - barH;
                // Gradient: steel-blue at the base, brighter at the peak.
                const grad = ctx.createLinearGradient(0, h, 0, y);
                grad.addColorStop(0, palette.accentDim);
                grad.addColorStop(1, palette.accent);
                ctx.fillStyle = grad;
                ctx.fillRect(i * barW + 1, y, barW - 2, barH);
            }
            rafRef.current = requestAnimationFrame(draw);
        };
        if (playing) {
            rafRef.current = requestAnimationFrame(draw);
        }
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        };
    }, [playing]);

    // 5. Canvas HiDPI sizing — set the backing store to devicePixelRatio
    //    so bars stay crisp on retina.
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const resize = () => {
            const rect = canvas.getBoundingClientRect();
            const dpr = window.devicePixelRatio || 1;
            canvas.width = rect.width * dpr;
            canvas.height = rect.height * dpr;
        };
        resize();
        window.addEventListener("resize", resize);
        return () => window.removeEventListener("resize", resize);
    }, []);

    // 6. Track completion → advance the queue. When empty, reshuffle
    //    the full catalogue so playback is endless.
    const onEnded = useCallback(() => {
        setQueue((q) => {
            if (q.length) {
                const [next, ...rest] = q;
                setCurrent(next);
                return rest;
            }
            if (tracks && tracks.length) {
                const [first, ...rest] = _shuffle(tracks);
                setCurrent(first);
                return rest;
            }
            setPlaying(false);
            return [];
        });
    }, [tracks]);

    const togglePlay = useCallback(() => {
        if (!audioRef.current || !current) return;
        _ensureAnalyser();
        // Chrome sometimes suspends the context on tab-focus loss;
        // resume defensively.
        if (audioCtxRef.current?.state === "suspended") {
            audioCtxRef.current.resume().catch(() => {});
        }
        if (audioRef.current.paused) {
            audioRef.current.play().then(() => setPlaying(true)).catch((err) => {
                // Iter 31.286 — AbortError = interrupted by a load
                // (rapid next/skip during buffer). Not a real failure;
                // the follow-up canplay listener will re-fire play().
                // NotAllowedError = Chrome autoplay policy; leave paused.
                if (err && (err.name === "AbortError"
                            || err.name === "NotAllowedError")) return;
                setError(`playback blocked: ${err?.message || err}`);
            });
        } else {
            audioRef.current.pause();
            setPlaying(false);
        }
    }, [current, _ensureAnalyser]);

    const stop = useCallback(() => {
        if (!audioRef.current) return;
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        setPlaying(false);
    }, []);

    const toggleMute = useCallback(() => {
        if (!audioRef.current) return;
        const next = !audioRef.current.muted;
        audioRef.current.muted = next;
        setMuted(next);
    }, []);

    const nextTrack = useCallback(() => {
        // Same wiring as "ended" — advance queue or reshuffle.
        onEnded();
    }, [onEnded]);

    // 7. Cover art URL — Iter 31.316 fallback chain via resolveCover():
    //    current track's `cover_url` → station's `cover_url` (when a
    //    station is selected) → default brand image. Always returns a
    //    string so the <img> tag has a src.
    const coverUrl = useMemo(
        () => resolveCover(current, station),
        [current, station],
    );

    return (
        <div
            data-testid="spy-radio-player"
            style={{
                minHeight: "100vh",
                width: "100%",
                background: palette.bg,
                color: palette.ink,
                fontFamily: '"Space Grotesk", system-ui, sans-serif',
                padding: "3rem 1.5rem",
                // Iter 31.281 · design polish — fill any wider viewport so
                // the parent site background can't tile through on
                // > 720px screens (testing_agent flagged this).
                position: "absolute",
                inset: 0,
                overflowY: "auto",
            }}
        >
            <div style={{ maxWidth: 720, margin: "0 auto" }}>
                <header style={{ marginBottom: "3rem" }}>
                    <div
                        style={{
                            fontFamily: '"JetBrains Mono", monospace',
                            fontSize: "0.7rem",
                            letterSpacing: "0.3em",
                            color: palette.inkSoft,
                            textTransform: "uppercase",
                            marginBottom: "0.5rem",
                        }}
                        data-testid="spy-radio-eyebrow"
                    >
                        {identity.eyebrow}
                    </div>
                    <h1
                        style={{
                            fontFamily: '"Space Grotesk", sans-serif',
                            fontSize: "2.75rem",
                            fontWeight: 700,
                            letterSpacing: "-0.02em",
                            margin: 0,
                            color: palette.ink,
                        }}
                        data-testid="spy-radio-title"
                    >
                        {identity.name}
                    </h1>
                    {identity.voice ? (
                        <p
                            style={{
                                fontFamily: '"JetBrains Mono", monospace',
                                fontSize: "0.8rem",
                                color: palette.inkSoft,
                                margin: "0.75rem 0 0",
                                maxWidth: "48ch",
                                lineHeight: 1.5,
                            }}
                            data-testid="spy-radio-voice"
                        >
                            {identity.voice}
                        </p>
                    ) : null}
                </header>

                {error ? (
                    <div
                        data-testid="spy-radio-error"
                        style={{
                            fontFamily: '"JetBrains Mono", monospace',
                            fontSize: "0.8rem",
                            color: "#DC5A5A",
                            padding: "1rem",
                            border: `1px solid ${palette.line}`,
                            borderRadius: "4px",
                        }}
                    >
                        {error}
                    </div>
                ) : tracks === null ? (
                    <div
                        data-testid="spy-radio-loading"
                        style={{
                            fontFamily: '"JetBrains Mono", monospace',
                            fontSize: "0.8rem",
                            color: palette.inkSoft,
                            letterSpacing: "0.15em",
                        }}
                    >
                        loading catalogue…
                    </div>
                ) : tracks.length === 0 ? (
                    <div
                        data-testid="spy-radio-empty"
                        style={{
                            fontFamily: '"JetBrains Mono", monospace',
                            fontSize: "0.85rem",
                            color: palette.inkSoft,
                            padding: "2rem",
                            border: `1px solid ${palette.line}`,
                            borderRadius: "4px",
                            textAlign: "center",
                        }}
                    >
                        No tracks on the station yet.
                    </div>
                ) : (
                    <div>
                        <div
                            style={{
                                display: "flex",
                                gap: "1.5rem",
                                alignItems: "flex-start",
                                marginBottom: "2rem",
                            }}
                        >
                            {coverUrl ? (
                                <img
                                    src={coverUrl}
                                    alt={current?.title || "cover"}
                                    data-testid="spy-radio-cover"
                                    style={{
                                        width: 120,
                                        height: 120,
                                        borderRadius: "4px",
                                        border: `1px solid ${palette.line}`,
                                        objectFit: "cover",
                                        filter: playing ? "none" : "grayscale(0.4) brightness(0.6)",
                                        transition: "filter 250ms",
                                    }}
                                />
                            ) : (
                                <div
                                    style={{
                                        width: 120,
                                        height: 120,
                                        border: `1px solid ${palette.line}`,
                                        borderRadius: "4px",
                                    }}
                                    aria-hidden="true"
                                />
                            )}
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div
                                    style={{
                                        fontFamily: '"JetBrains Mono", monospace',
                                        fontSize: "0.65rem",
                                        letterSpacing: "0.28em",
                                        color: palette.inkSoft,
                                        textTransform: "uppercase",
                                        marginBottom: "0.35rem",
                                    }}
                                    data-testid="spy-radio-now-playing-label"
                                >
                                    Now Playing
                                </div>
                                <div
                                    style={{
                                        fontFamily: '"Space Grotesk", sans-serif',
                                        fontSize: "1.5rem",
                                        fontWeight: 600,
                                        color: palette.ink,
                                        marginBottom: "0.5rem",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                    }}
                                    data-testid="spy-radio-track-title"
                                >
                                    {current?.title || "—"}
                                </div>
                                <div
                                    style={{
                                        fontFamily: '"JetBrains Mono", monospace',
                                        fontSize: "0.7rem",
                                        color: palette.accent,
                                        letterSpacing: "0.15em",
                                    }}
                                    data-testid="spy-radio-track-brand"
                                >
                                    {(current?.brand || "").toUpperCase().replace(/_/g, " ")}
                                </div>
                            </div>
                        </div>

                        {/* Spectrum visualiser */}
                        <canvas
                            ref={canvasRef}
                            data-testid="spy-radio-spectrum"
                            style={{
                                width: "100%",
                                height: 96,
                                display: "block",
                                marginBottom: "1.5rem",
                                border: `1px solid ${palette.line}`,
                                borderRadius: "4px",
                                background: "#0A0E14",
                            }}
                        />

                        {/* Controls */}
                        <div
                            style={{
                                display: "flex",
                                gap: "0.75rem",
                                marginBottom: "3rem",
                            }}
                        >
                            <button
                                type="button"
                                onClick={togglePlay}
                                data-testid="spy-radio-play-toggle"
                                style={{
                                    background: playing ? palette.accent : "transparent",
                                    color: playing ? palette.bg : palette.accent,
                                    border: `1px solid ${palette.accent}`,
                                    padding: "0.75rem 1.75rem",
                                    fontFamily: '"JetBrains Mono", monospace',
                                    fontSize: "0.75rem",
                                    letterSpacing: "0.2em",
                                    textTransform: "uppercase",
                                    borderRadius: "3px",
                                    cursor: "pointer",
                                    transition: "background 150ms, color 150ms",
                                }}
                            >
                                {playing ? "◼ Pause" : "▶ Play"}
                            </button>
                            <button
                                type="button"
                                onClick={stop}
                                data-testid="spy-radio-stop"
                                style={{
                                    background: "transparent",
                                    color: palette.inkSoft,
                                    border: `1px solid ${palette.line}`,
                                    padding: "0.75rem 1.25rem",
                                    fontFamily: '"JetBrains Mono", monospace',
                                    fontSize: "0.75rem",
                                    letterSpacing: "0.2em",
                                    textTransform: "uppercase",
                                    borderRadius: "3px",
                                    cursor: "pointer",
                                }}
                            >
                                Stop
                            </button>
                            <button
                                type="button"
                                onClick={nextTrack}
                                data-testid="spy-radio-next"
                                style={{
                                    background: "transparent",
                                    color: palette.inkSoft,
                                    border: `1px solid ${palette.line}`,
                                    padding: "0.75rem 1.25rem",
                                    fontFamily: '"JetBrains Mono", monospace',
                                    fontSize: "0.75rem",
                                    letterSpacing: "0.2em",
                                    textTransform: "uppercase",
                                    borderRadius: "3px",
                                    cursor: "pointer",
                                }}
                            >
                                Skip
                            </button>
                            <button
                                type="button"
                                onClick={toggleMute}
                                data-testid="spy-radio-mute"
                                style={{
                                    background: muted ? palette.line : "transparent",
                                    color: muted ? palette.ink : palette.inkSoft,
                                    border: `1px solid ${palette.line}`,
                                    padding: "0.75rem 1.25rem",
                                    fontFamily: '"JetBrains Mono", monospace',
                                    fontSize: "0.75rem",
                                    letterSpacing: "0.2em",
                                    textTransform: "uppercase",
                                    borderRadius: "3px",
                                    cursor: "pointer",
                                    marginLeft: "auto",
                                }}
                            >
                                {muted ? "Unmute" : "Mute"}
                            </button>
                        </div>

                        {/* Catalogue tail */}
                        <div
                            data-testid="spy-radio-catalogue"
                            style={{
                                borderTop: `1px solid ${palette.line}`,
                                paddingTop: "1.5rem",
                            }}
                        >
                            <div
                                style={{
                                    fontFamily: '"JetBrains Mono", monospace',
                                    fontSize: "0.65rem",
                                    letterSpacing: "0.28em",
                                    color: palette.inkSoft,
                                    textTransform: "uppercase",
                                    marginBottom: "1rem",
                                }}
                            >
                                Catalogue · {tracks.length}
                            </div>
                            <ol
                                style={{
                                    listStyle: "none",
                                    padding: 0,
                                    margin: 0,
                                    fontFamily: '"JetBrains Mono", monospace',
                                    fontSize: "0.8rem",
                                }}
                            >
                                {tracks.map((t) => (
                                    <li
                                        key={t.id}
                                        data-testid={`spy-radio-track-${t.catalogue_id || t.id}`}
                                        style={{
                                            padding: "0.5rem 0",
                                            borderBottom: `1px solid ${palette.line}`,
                                            display: "flex",
                                            justifyContent: "space-between",
                                            color:
                                                current?.id === t.id
                                                    ? palette.accent
                                                    : palette.inkSoft,
                                            cursor: "pointer",
                                        }}
                                        onClick={() => {
                                            setCurrent(t);
                                            setQueue((_q) => _shuffle(tracks.filter((x) => x.id !== t.id)));
                                        }}
                                    >
                                        <span>{t.title}</span>
                                        <span style={{ letterSpacing: "0.1em" }}>
                                            {t.brand?.replace(/_/g, " ")}
                                        </span>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </div>
                )}

                {/* Hidden audio element — controlled entirely via ref. */}
                <audio
                    ref={audioRef}
                    data-testid="spy-radio-audio"
                    onEnded={onEnded}
                    onPlay={() => setPlaying(true)}
                    onPause={() => setPlaying(false)}
                    onError={() => setError("stream error — track skipped")}
                    crossOrigin="anonymous"
                    preload="metadata"
                />
            </div>
        </div>
    );
}
